import * as d3 from 'd3';
import { getRowerColorByKey } from '../../../../../utils/rowerColors';
import { chartTooltipHtml as tt } from '../../common/chartTooltipClasses';
import type { DisplayLine, TracePoint } from '../utils/types';
import type { ChartDimensions, ChartElements } from './types';

interface TooltipContext {
  elements: ChartElements;
  tooltipElement: HTMLDivElement | null;
  getDimensions: () => ChartDimensions | null;
  getCurrentXScale: () => d3.ScaleLinear<number, number>;
  getTooltipLines: () => DisplayLine[];
}

export function setupTooltip(ctx: TooltipContext): () => void {
  const tooltip = d3.select(ctx.tooltipElement);
  const bisect = d3.bisector<TracePoint, number>((point) => point.x).left;
  let pendingFrame: number | null = null;
  let latestEvent: PointerEvent | null = null;

  const hideTooltip = () => {
    tooltip.style('opacity', 0);
    ctx.elements.crosshair.style('opacity', 0);
  };

  const handlePointerMove = (event: PointerEvent) => {
    const dims = ctx.getDimensions();
    if (!dims) return;

    const pointer = d3.pointer(event, ctx.elements.root.node());
    const [px, py] = pointer;

    if (px < 0 || px > dims.plotWidth || py < 0 || py > dims.plotHeight) {
      hideTooltip();
      return;
    }

    ctx.elements.crosshair.attr('x1', px).attr('x2', px).style('opacity', 0.6);

    const currentX = ctx.getCurrentXScale();
    const angle = currentX.invert(px);
    const lines = ctx.getTooltipLines();

    const samples: { rower: string; label: string; force: number }[] = [];

    for (const line of lines) {
      const index = bisect(line.data, angle);
      const prev = line.data[Math.max(0, index - 1)];
      const next = line.data[Math.min(line.data.length - 1, index)];
      const candidate = !prev
        ? next
        : !next
          ? prev
          : Math.abs(prev.x - angle) < Math.abs(next.x - angle)
            ? prev
            : next;
      if (!candidate) continue;
      samples.push({
        rower: line.rower,
        label: line.label,
        force: candidate.y,
      });
    }

    if (!samples.length) {
      hideTooltip();
      return;
    }

    const isSingleRower = new Set(samples.map((s) => s.rower)).size <= 1;
    let tooltipHtml: string;

    if (isSingleRower && samples.length > 5) {
      const forces = samples.map((s) => s.force);
      const min = Math.min(...forces);
      const max = Math.max(...forces);
      const mean = forces.reduce((a, b) => a + b, 0) / forces.length;
      const rower = samples[0].rower;
      const color = getRowerColorByKey(rower);

      tooltipHtml = `
        <div class="${tt.title}">Angle: ${Math.round(angle)}°</div>
        <div class="${tt.row}">
          <span class="${tt.rowInner}">
            <span class="inline-block h-2 w-2 rounded-full" style="background:${color}"></span>
            <span class="${tt.label}">${rower.replace('Rower ', 'Seat ')} · ${samples.length} strokes</span>
          </span>
        </div>
        <div class="${tt.grid}">
          <span class="${tt.label}">Mean</span><span class="${tt.value}">${Math.round(mean)} kgf</span>
          <span class="${tt.label}">Max</span><span class="${tt.value}">${Math.round(max)} kgf</span>
          <span class="${tt.label}">Min</span><span class="${tt.value}">${Math.round(min)} kgf</span>
        </div>`;
    } else {
      const display = samples.slice(0, 8);
      tooltipHtml = `
        <div class="${tt.title}">Angle: ${Math.round(angle)}°</div>
        ${display
          .map(
            (row) => `
              <div class="${tt.row}">
                <span class="${tt.rowInner}">
                  <span class="inline-block h-2 w-2 rounded-full" style="background:${getRowerColorByKey(row.rower)}"></span>
                  <span class="${tt.label}">${row.label.replace('Rower ', 'R')}</span>
                </span>
                <span class="${tt.value}">${Math.round(row.force)} kgf</span>
              </div>`
          )
          .join('')}
        ${samples.length > 8 ? `<div class="${tt.muted}">+${samples.length - 8} more</div>` : ''}`;
    }

    const tooltipWidth = ctx.tooltipElement?.offsetWidth || 200;
    const tooltipHeight = ctx.tooltipElement?.offsetHeight || 60;
    const edge = 8;
    let left = dims.margin.left + px + 16;
    let top = dims.margin.top + py - 10;

    if (left + tooltipWidth > dims.width - edge) {
      left = dims.margin.left + px - tooltipWidth - 16;
    }
    if (left < edge) left = edge;
    if (top < edge) top = edge;
    if (top + tooltipHeight > dims.height - edge) {
      top = dims.height - tooltipHeight - edge;
    }

    tooltip
      .style('opacity', 1)
      .style('left', `${left}px`)
      .style('top', `${top}px`)
      .html(tooltipHtml);
  };

  const onPointerMove = (event: PointerEvent) => {
    latestEvent = event;
    if (pendingFrame) return;
    pendingFrame = requestAnimationFrame(() => {
      if (latestEvent) handlePointerMove(latestEvent);
      pendingFrame = null;
    });
  };

  const onPointerLeave = () => {
    hideTooltip();
    latestEvent = null;
    if (pendingFrame) {
      cancelAnimationFrame(pendingFrame);
      pendingFrame = null;
    }
  };

  ctx.elements.interactionLayer
    .on('pointermove', onPointerMove as any)
    .on('pointerleave', onPointerLeave as any);

  return () => {
    if (pendingFrame) cancelAnimationFrame(pendingFrame);
    latestEvent = null;
    ctx.elements.interactionLayer.on('pointermove', null).on('pointerleave', null);
    tooltip.style('opacity', 0);
  };
}
